// Nine Lives '84 - Tabs.
//
// Running builds speed; a jump from speed keeps it (a running jump), while
// a jump from a standstill is a short hop with little horizontal travel,
// as in the original. Coyote time and a jump buffer soften the timing
// without changing the reach. Down + jump drops through a one-way platform.

import {
  AIR_ACCEL,
  CAT_H,
  CAT_W,
  COYOTE_TICKS,
  DROP_TICKS,
  DT,
  FRICTION,
  HOP_VX,
  JUMP_BUFFER_TICKS,
  JUMP_VY,
  K_DOWN,
  K_JUMP,
  K_LEFT,
  K_RIGHT,
  RUN_ACCEL,
  RUN_JUMP_SPEED,
  RUN_MAX,
} from './constants';
import { approach, stepBody } from './physics';
import type { Body, GameEvent, KnockedBy, Platform } from './types';

export type CatState = 'alive' | 'caught';

export interface Cat extends Body {
  facing: 1 | -1;
  coyote: number;
  jumpBuffer: number;
  dropTicks: number;
  /** True while airborne from a running jump (views stretch the pose). */
  leaping: boolean;
  state: CatState;
  /** Ticks of invulnerability left after a respawn. */
  invuln: number;
  /** Ticks since Tabs was last knocked (views tumble him briefly). */
  knockedTicks: number;
}

export function newCat(x: number, y: number): Cat {
  return {
    x,
    y,
    w: CAT_W,
    h: CAT_H,
    vx: 0,
    vy: 0,
    // Spawns standing, so a jump on the very first tick works.
    onGround: true,
    ground: null,
    facing: 1,
    coyote: COYOTE_TICKS,
    jumpBuffer: 0,
    dropTicks: 0,
    leaping: false,
    state: 'alive',
    invuln: 0,
    knockedTicks: 0,
  };
}

/** Throw Tabs off whatever he is standing on. */
export function knockCat(
  cat: Cat,
  vx: number,
  vy: number,
  by: KnockedBy,
  events: GameEvent[],
): void {
  cat.vx = vx;
  cat.vy = vy;
  cat.onGround = false;
  cat.ground = null;
  cat.coyote = 0;
  cat.jumpBuffer = 0;
  cat.dropTicks = DROP_TICKS;
  cat.leaping = false;
  cat.knockedTicks = 30;
  events.push({ kind: 'knocked', by });
}

export function updateCat(
  cat: Cat,
  keys: number,
  platforms: readonly Platform[],
  events: GameEvent[],
): void {
  if (cat.invuln > 0) cat.invuln--;
  if (cat.knockedTicks > 0) cat.knockedTicks--;

  const dir = keys & K_RIGHT ? 1 : keys & K_LEFT ? -1 : 0;
  if (dir) cat.facing = dir;

  if (cat.onGround) {
    cat.vx = dir
      ? approach(cat.vx, dir * RUN_MAX, RUN_ACCEL * DT)
      : approach(cat.vx, 0, FRICTION * DT);
  } else if (dir) {
    cat.vx = approach(cat.vx, dir * RUN_MAX, AIR_ACCEL * DT);
  }

  if (keys & K_JUMP) cat.jumpBuffer = JUMP_BUFFER_TICKS;
  else if (cat.jumpBuffer > 0) cat.jumpBuffer--;
  if (cat.onGround) cat.coyote = COYOTE_TICKS;
  else if (cat.coyote > 0) cat.coyote--;
  if (cat.dropTicks > 0) cat.dropTicks--;

  const wasOnGround = cat.onGround;
  if (cat.jumpBuffer > 0 && cat.coyote > 0) {
    cat.jumpBuffer = 0;
    cat.coyote = 0;
    if (keys & K_DOWN && cat.ground?.oneWay) {
      cat.dropTicks = DROP_TICKS;
    } else {
      cat.vy = JUMP_VY;
      if (Math.abs(cat.vx) >= RUN_JUMP_SPEED) {
        cat.leaping = true;
      } else {
        cat.vx = dir * HOP_VX;
        cat.leaping = false;
      }
      cat.onGround = false;
      cat.ground = null;
      events.push({ kind: 'jump' });
    }
  }

  stepBody(cat, platforms, DT, cat.dropTicks > 0);

  if (cat.onGround) {
    cat.leaping = false;
    if (!wasOnGround) events.push({ kind: 'land' });
  }
}
